import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingIndicator } from '../LoadingIndicator';
import { PointStyleForm, TemplateForm } from './PointStyleForm';
import { useTemplates } from '../../hooks/api/use-templates.hook';
import { PointStyleApiModel, PointStyleRelationship, WithTemplates } from '../../../../types/models/point-style.model';
import {
  useCreatePointStyle,
  useUpdatePointStyle,
  useGetPointStyle,
  useAddTemplateToPointStyle,
  useRemoveTemplateFromPointStyle,
} from '../../hooks/api/use-point-styles.hook';
import { getDirtyValues } from '../../utils/react-hook-form';

export const AddEditPointStyle = () => {
  const navigate = useNavigate();
  const { pointStyleId } = useParams<{ pointStyleId: string }>();

  const {
    pointStyle,
    loading: loadingPointStyle,
    error: gettingError,
  } = useGetPointStyle<PointStyleApiModel & WithTemplates>({
    pointStyleId: Number(pointStyleId),
    include: [PointStyleRelationship.TEMPLATES],
  });
  const [createPointStyle, { loading: creatingPointStyle, error: createError }] = useCreatePointStyle();
  const [updatePointStyle, { loading: updatingPointStyle, error: updateError }] = useUpdatePointStyle();
  const [addTemplateToPointStyle] = useAddTemplateToPointStyle();
  const [removeTemplateFromPointStyle] = useRemoveTemplateFromPointStyle();
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<PointStyleForm>({
    resolver: classValidatorResolver(PointStyleForm),
    values: pointStyle,
  });

  const onSubmit: SubmitHandler<PointStyleForm> = async (submittedValues) => {
    if (!pointStyle) {
      const { templates, ...values } = submittedValues;
      await createPointStyle({ ...values, templateIds: templates.map(({ id }) => id) });
      return;
    }

    const { templates, ...values } = getDirtyValues(dirtyFields, submittedValues);
    const templatesToAdd =
      templates?.filter(({ id }) => !pointStyle.templates.find((template) => template.id === id)) ?? [];
    const templatesToRemove = templates
      ? pointStyle.templates.filter(({ id }) => !templates?.find((template) => template.id === id))
      : [];

    await Promise.all([
      updatePointStyle({ ...values, pointStyleId: pointStyle.id }),
      ...templatesToAdd.map(({ id: templateId }) =>
        addTemplateToPointStyle({ pointStyleId: pointStyle.id, templateId }),
      ),
      ...templatesToRemove.map(({ id: templateId }) =>
        removeTemplateFromPointStyle({ pointStyleId: pointStyle.id, templateId }),
      ),
    ]);

    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    const isDisabled = creatingPointStyle || updatingPointStyle;

    return (
      <div>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={isDisabled}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <Controller
          control={control}
          name="templates"
          render={({ field }) => (
            <Autocomplete
              {...register('templates')}
              loading={loadingTemplates}
              sx={{ mt: '1rem' }}
              multiple
              onChange={(_event, selectedOptions) => field.onChange([...selectedOptions])}
              onBlur={field.onBlur}
              value={field.value}
              getOptionLabel={(template) => (template as TemplateForm)?.name ?? ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              options={templatesResponse?.pages.flatMap(({ result }) => result) ?? []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template(s)"
                  error={Boolean(errors.templates)}
                  helperText={errors.templates?.message}
                />
              )}
            />
          )}
        />
      </div>
    );
  };

  if (loadingPointStyle) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingPointStyle ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
          <DialogTitle>{`${pointStyle ? 'Edit' : 'Add'} Civil 3D Point Style`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {pointStyle ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
